import React from "react";
import { useIntl } from "react-intl";
import { Column } from "@webbio/components";

import { DescriptionRunResultDTO, DescriptionRunResultDTOStatusEnum } from "@app/api/generated";
import { LabelComponent } from "@app/core";
import { Table } from "@app/components/table/table";
import { getPlatformName } from "@app/util/platform-name";
import { useProcessedDate } from "@app/util/use-processed-date";

import { RUN_STATUS_LABEL_KEY } from "@app/util/run-status";
import { RunCounterSummary } from "@app/components/run-counter-summary/run-counter-summary";
import { RUN_COUNTERS } from "../run-counters";

interface IRunsTableProps {
  runs: DescriptionRunResultDTO[];
  isLoading: boolean;
  onRowClick: (run: DescriptionRunResultDTO) => void;
}

const RunsTable = ({ runs, isLoading, onRowClick }: IRunsTableProps): JSX.Element => {
  const intl = useIntl();
  const formatProcessedDate = useProcessedDate();

  const onTdClick = (run: DescriptionRunResultDTO) => onRowClick(run);

  /**
   * Four decimals: a run of 200 descriptions costs about nine cents, so two would read as €0,09.
   * Dollars because that is what Google bills us in.
   */
  const formatCost = (costUsd: number): string =>
    intl.formatNumber(costUsd, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    });

  return (
    <Table dataSource={runs} isLoading={isLoading}>
      <Column
        field="runId"
        title={intl.formatMessage({ id: "productDescription.table.id" })}
        width={70}
        onTdClick={onTdClick}
      />
      <Column
        field="status"
        onTdClick={onTdClick}
        title={intl.formatMessage({ id: "productDescription.table.status" })}
        maxWidth={80}
        width={80}
        cell={(status: DescriptionRunResultDTOStatusEnum) => (
          <LabelComponent
            type={status.toLowerCase()}
            label={intl.formatMessage({ id: RUN_STATUS_LABEL_KEY[status] })}
          />
        )}
      />
      <Column
        field="platformId"
        onTdClick={onTdClick}
        title={intl.formatMessage({ id: "productDescription.table.platform" })}
        cell={(platformId: number) => <>{getPlatformName(intl, platformId)}</>}
        maxWidth={90}
        width={90}
      />
      <Column
        field="finishedAt"
        onTdClick={onTdClick}
        title={intl.formatMessage({ id: "productDescription.table.finishedAt" })}
        cell={(finishedAt: Date | null) => (
          <span>{finishedAt ? <>{formatProcessedDate(new Date(finishedAt))}</> : <>—</>}</span>
        )}
      />
      <Column
        field="estimatedCostUsd"
        onTdClick={onTdClick}
        title={intl.formatMessage({ id: "productDescription.table.cost" })}
        maxWidth={90}
        width={90}
        cell={(costUsd: number) => <span>{formatCost(costUsd ?? 0)}</span>}
      />
      {/* `field` is required by Column but only feeds the cell's first argument; this summary needs
          the whole row, which arrives as the third. Hence a borrowed field, same as the sync table
          in `product-page-sync/components/runs-table.tsx`. */}
      <Column
        field="runId"
        title=""
        isNotSortable
        onTdClick={onTdClick}
        cell={(_runId: number, _index: number, run: DescriptionRunResultDTO) => (
          <RunCounterSummary run={run} counters={RUN_COUNTERS} />
        )}
      />
    </Table>
  );
};

export { RunsTable };
